/**
 * An integration's own page: what it is, whether it is connected for the
 * organization, the prompts that show what it is for, and the setup — the
 * firm's credentials for an api-key connector, the platform's OAuth for the
 * rest. Access (on/off for the organization) sits below, separate from
 * connection, because "connected" and "available to everyone" are different
 * decisions.
 *
 * @module components/apps/integrations/IntegrationDetailPage
 */
import { Link, Navigate } from "@tanstack/react-router";
import { ArrowRightIcon, ChevronRightIcon, Loader2Icon, PlugIcon, PlugZapIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { cn } from "~/lib/utils";
import { useStartChatWithText } from "../../../hooks/useStartChatWithText";
import {
  usesWorkspaceOAuth,
  useWorkspaceIntegrationOAuth,
  workspaceOAuthAvailable,
} from "../../../state/workspaceIntegrationOAuth";
import { useIntegrations } from "../../../state/governance";
import { useOrgProfile } from "../../../state/orgProfile";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { ListSkeleton } from "../../regulation/LoadingSkeletons";
import { IntegrationIcon } from "./IntegrationIcon";
import {
  INTEGRATION_SCOPE_SPECS,
  integrationConnectsWith,
  integrationDeveloper,
  integrationPrompts,
  integrationStatus,
  integrationTagline,
} from "./integrationContent";
import { IntegrationScopeForm } from "./IntegrationScopeForm";
import { IntegrationSetupForm, SetupGuideMark } from "./IntegrationSetupForm";

/** The prompt chips fan out rather than stacking. */
const CHIP_OFFSETS = ["sm:translate-x-10", "sm:-translate-x-6", "sm:translate-x-4"];

const formatDate = (value: string | null): string => {
  if (value === null) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

function SectionHeading({ title, count }: { readonly title: string; readonly count?: number }) {
  return (
    <h2 className="text-base font-semibold tracking-tight text-foreground">
      {title}
      {count !== undefined ? (
        <span className="ml-2 text-sm font-normal tabular-nums text-muted-foreground">{count}</span>
      ) : null}
    </h2>
  );
}

interface IntegrationDetailPageProps {
  readonly organizationId: string | null;
  readonly orgSegment: string;
  readonly integrationId: string;
}

export function IntegrationDetailPage(props: IntegrationDetailPageProps) {
  // iManage is an area of its own (routes/$org.apps.imanage*).
  return props.integrationId === "imanage" ? (
    <Navigate to="/$org/apps/imanage" params={{ org: props.orgSegment }} replace />
  ) : (
    <AvailableIntegrationDetailPage {...props} />
  );
}

function AvailableIntegrationDetailPage({
  organizationId,
  orgSegment,
  integrationId,
}: IntegrationDetailPageProps) {
  const { snapshot, isPending, setEnabled, setCredentials, clearCredentials, setScope } =
    useIntegrations(organizationId);
  const { canManage } = useOrgProfile(organizationId);

  const catalogIntegration =
    snapshot?.integrations.find((candidate) => candidate.id === integrationId) ?? null;

  // Composio OAuth and partner OAuth (Altruist) both connect through the
  // workspace sign-in flow, whose snapshot is the live connection state.
  const workspaceOAuth =
    catalogIntegration !== null && usesWorkspaceOAuth(catalogIntegration.contract);
  const oauth = useWorkspaceIntegrationOAuth(workspaceOAuth ? organizationId : null);

  const oauthAccount = oauth.snapshot?.accounts.find(
    (account) => account.connectorId === integrationId,
  );
  const integration =
    catalogIntegration && workspaceOAuth && oauth.snapshot
      ? {
          ...catalogIntegration,
          connected: oauthAccount?.status === "connected",
          connectionStatus: oauthAccount?.status ?? "disconnected",
          enabled: oauthAccount?.enabled ?? false,
        }
      : catalogIntegration;

  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh form per integration: values never leak between connectors.
  // biome-ignore lint/correctness/useExhaustiveDependencies: changing integration resets the credential form.
  useEffect(() => {
    setValues({});
    setError(null);
  }, [integrationId]);

  const run = useCallback(async (action: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    const message = await action();
    setBusy(false);
    setError(message);
    return message;
  }, []);

  // A stored credential leaves no trace in the form: the next entry starts
  // clean, and the other way in cannot inherit what was just sent.
  const saveCredentials = useCallback(
    async (chosen: Record<string, string>) => {
      const message = await run(() => setCredentials(integrationId, chosen));
      if (message === null) setValues({});
      return message;
    },
    [integrationId, run, setCredentials],
  );

  // Writes the prompt into the org's new-chat draft, then opens it.
  const startChat = useStartChatWithText(organizationId);
  const tryPrompt = useCallback(
    (prompt: string) => {
      if (startChat) void startChat(prompt);
    },
    [startChat],
  );

  if (integration === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        {isPending && snapshot === null ? (
          <div className="w-full max-w-md">
            <ListSkeleton rows={4} />
          </div>
        ) : (
          <>
            <PlugZapIcon className="size-6 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">Integration not found</p>
            <Button
              variant="outline"
              className="cursor-pointer rounded-full"
              render={<Link to="/$org/apps" params={{ org: orgSegment }} />}
            >
              Back to apps
            </Button>
          </>
        )}
      </div>
    );
  }

  const contract = integration.contract ?? "oauth";
  const partner = contract === "partner-oauth";
  // For a partner integration: can this server start its sign-in (it holds
  // the vendor's partner credentials)? False means awaiting partner access.
  const partnerAccess = partner ? workspaceOAuthAvailable(oauth.snapshot, integration) : null;
  const status = integrationStatus(integration, partnerAccess);
  const configured = integration.configured === true;
  const scopeSpec = INTEGRATION_SCOPE_SPECS[integration.id] ?? null;
  const prompts = integrationPrompts(integration);
  const canStartOauth = workspaceOAuthAvailable(oauth.snapshot, integration) === true;
  const connectOauth = (action: "connect" | "reconnect" | "disconnect" = "connect") =>
    run(() => oauth.runAction(integration.id, action));

  return (
    <div className="regulation-page-scroll-fade min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
        <nav className="flex items-center gap-1.5 text-sm">
          <Link
            to="/$org/apps"
            params={{ org: orgSegment }}
            className="cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
          >
            Apps
          </Link>
          <ChevronRightIcon className="size-3.5 text-muted-foreground/60" />
          <span className="font-medium text-foreground">{integration.name}</span>
        </nav>

        <IntegrationIcon integration={integration} className="mt-8 size-12 rounded-xl p-2" />

        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {integration.name}
            </h1>
            <p className="mt-1.5 text-muted-foreground">{integrationTagline(integration)}</p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {integration.connected ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/10 px-3 py-1.5 text-xs text-success">
                <span aria-hidden className="size-1.5 rounded-full bg-success" />
                <span className="font-medium">Connected for this organization</span>
              </span>
            ) : canManage && (contract === "oauth" || (partner && partnerAccess === true)) ? (
              <Button
                className="cursor-pointer rounded-full"
                disabled={busy || !canStartOauth || integration.connectionStatus === "pending"}
                title={
                  canStartOauth ? undefined : "Not available to connect from this workspace yet"
                }
                onClick={() => void connectOauth()}
              >
                {busy ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <PlugIcon className="size-4" />
                )}
                {partner ? `Sign in to ${integration.name}` : "Connect for workspace"}
              </Button>
            ) : canManage && contract === "api-key" && !configured ? (
              <Button className="cursor-pointer rounded-full" render={<a href="#setup" />}>
                <PlugIcon className="size-4" />
                Connect
              </Button>
            ) : (
              <span
                className={cn("rounded-full px-3 py-1.5 text-xs font-medium", status.className)}
              >
                {status.label}
              </span>
            )}
          </div>
        </div>

        {contract === "oauth" || partner ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              {partner
                ? `An organization admin signs in with their ${integration.name} login; everyone in the organization can then read what that login can see through Eve.`
                : "Connect a company account for authorized workspace members to use through Eve."}
            </p>
            {oauthAccount?.accountLabel ? (
              <p className="text-sm">Account: {oauthAccount.accountLabel}</p>
            ) : null}
            {integration.connectionStatus === "pending" ? (
              <p className="text-sm">Waiting for sign-in. Finish in the provider tab.</p>
            ) : null}
            {canManage && integration.connectionStatus !== "disconnected" ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={busy || !canStartOauth}
                  onClick={() => void connectOauth("reconnect")}
                >
                  Reconnect
                </Button>
                {/* Letting go never needs the partner credentials. */}
                <Button
                  variant="outline"
                  disabled={busy || (!canStartOauth && !partner)}
                  onClick={() => void connectOauth("disconnect")}
                >
                  {integration.connectionStatus === "pending" ? "Cancel connection" : "Disconnect"}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* What it is for, as things you can actually say — each chip opens a
            chat with that prompt already typed. */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-accent/60 via-muted/40 to-accent/40 px-6 py-12">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-4">
            {prompts.map((prompt, index) => (
              <button
                key={prompt}
                type="button"
                className={cn(
                  "flex max-w-full cursor-pointer items-center gap-2 rounded-full bg-background/95 py-2 pl-3 pr-2 shadow-sm backdrop-blur transition-transform hover:scale-[1.02]",
                  CHIP_OFFSETS[index % CHIP_OFFSETS.length],
                )}
                onClick={() => tryPrompt(prompt)}
              >
                <IntegrationIcon
                  integration={integration}
                  className="size-6 rounded-md border-0 p-0.5"
                />
                <span className="shrink-0 text-sm font-semibold text-foreground">
                  {integration.name}
                </span>
                <span className="truncate text-sm text-foreground">{prompt}</span>
                <span className="ml-1 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted transition-colors hover:bg-muted/70">
                  <ArrowRightIcon className="size-4" />
                </span>
              </button>
            ))}
          </div>
        </div>

        <p className="mt-8 max-w-2xl text-base leading-relaxed text-foreground">
          {integration.description}
        </p>

        {error ? (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <section id="setup" className="mt-10 scroll-mt-6">
          <div className="flex items-center justify-between gap-3">
            <SectionHeading title="Setup" />
            {contract === "api-key" ? (
              <SetupGuideMark connectorId={integration.id} integrationName={integration.name} />
            ) : null}
          </div>
          <div className="mt-3 rounded-2xl border border-border/60 bg-card p-5">
            {contract === "api-key" ? (
              configured ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Credentials are stored encrypted for this organization. Replace them by entering
                    new ones, or disconnect to forget them.
                  </p>
                  {canManage ? (
                    <>
                      <IntegrationSetupForm
                        connectorId={integration.id}
                        values={values}
                        onChange={setValues}
                        disabled={busy}
                        submitLabel="Replace credentials"
                        onSubmit={(chosen) => void saveCredentials(chosen)}
                      />
                      <div className="flex justify-end border-t border-border/60 pt-4">
                        <Button
                          variant="destructive-outline"
                          size="sm"
                          className="cursor-pointer rounded-full"
                          disabled={busy}
                          onClick={() => void run(() => clearCredentials(integration.id))}
                        >
                          Disconnect
                        </Button>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : canManage ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter what your IT team set up for Amoreg — each field says where to find it.
                    Saving checks the credential with {integration.name} and connects it for the
                    whole organization.
                  </p>
                  <IntegrationSetupForm
                    connectorId={integration.id}
                    values={values}
                    onChange={setValues}
                    disabled={busy}
                    submitLabel="Save and connect"
                    onSubmit={(chosen) => void saveCredentials(chosen)}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  An organization admin needs to supply {integration.name}'s credentials.
                </p>
              )
            ) : partner ? (
              <p className="text-sm text-muted-foreground">
                {partnerAccess === false
                  ? `Awaiting ${integration.name} partner access. ${integration.name} issues Amoreg's partner API credentials on request; until this deployment holds them, ${integration.name} cannot be connected. Nothing is needed from your firm yet.`
                  : canManage
                    ? `Use Sign in above with a ${integration.name} advisor or admin login. Amoreg receives read-only, prior-day data for the households that login can see, and renews its access automatically until an admin disconnects it.`
                    : `An organization admin connects ${integration.name} by signing in with their ${integration.name} login.`}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {integration.name} connects through the Amoreg platform's OAuth flow
                {canStartOauth
                  ? " — use Connect account above and sign in to the account the organization should use."
                  : ". Once the platform has linked an account, it can be turned on here."}
              </p>
            )}
          </div>
        </section>

        {scopeSpec !== null && contract === "api-key" ? (
          <section className="mt-10">
            <SectionHeading title="What Amoreg can reach" />
            <div className="mt-3 rounded-2xl border border-border/60 bg-card p-5">
              <IntegrationScopeForm
                key={`${integration.id}:${JSON.stringify(integration.scope ?? null)}`}
                organizationId={organizationId}
                connectorId={integration.id}
                configured={configured}
                spec={scopeSpec}
                scope={integration.scope ?? null}
                disabled={busy}
                canManage={canManage}
                onSave={(scope) => void run(() => setScope(integration.id, scope))}
              />
            </div>
          </section>
        ) : null}

        <section className="mt-10">
          <SectionHeading title="Access" />
          <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card px-5 py-4">
            <div>
              <p className="text-sm font-medium text-foreground">
                {integration.enabled ? "On for this organization" : "Off"}
              </p>
              <p className="text-xs text-muted-foreground">
                {integration.enabled
                  ? "Available to everyone in the organization."
                  : "The organization's agents cannot reach it."}
              </p>
            </div>
            {canManage &&
            (contract === "oauth" || (partner ? integration.connected : configured)) ? (
              <Switch
                checked={integration.enabled}
                disabled={busy}
                onCheckedChange={() =>
                  void run(async () => {
                    const result = await setEnabled(integration.id, !integration.enabled);
                    oauth.refresh();
                    return result;
                  })
                }
                aria-label={`${integration.enabled ? "Disable" : "Enable"} ${integration.name}`}
              />
            ) : null}
          </div>
        </section>

        {integration.capabilities.length > 0 ? (
          <section className="mt-10">
            <SectionHeading title="Capabilities" count={integration.capabilities.length} />
            <ul className="mt-3 flex flex-wrap gap-2">
              {integration.capabilities.map((capability) => (
                <li
                  key={capability}
                  className="rounded-full border border-border/60 bg-card px-3 py-1 text-sm text-foreground"
                >
                  {capability}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mt-10">
          <SectionHeading title="Information" />
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-8 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Developer</dt>
            <dd className="text-foreground">{integrationDeveloper(integration)}</dd>
            <dt className="text-muted-foreground">Connects with</dt>
            <dd className="text-foreground">
              {integrationConnectsWith({ id: integration.id, contract })}
            </dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd className="text-foreground">{status.label}</dd>
            <dt className="text-muted-foreground">Last connected</dt>
            <dd className="text-foreground">{formatDate(integration.lastConnectedAt)}</dd>
            <dt className="text-muted-foreground">Last synced</dt>
            <dd className="text-foreground">{formatDate(integration.lastSyncedAt)}</dd>
          </dl>
        </section>
      </div>
    </div>
  );
}
